import { useMemo, useCallback, useState } from 'react';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import DownloadIcon from '@mui/icons-material/Download';
import SyncIcon from '@mui/icons-material/Sync';
import Typography from '@mui/material/Typography';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Box from '@mui/material/Box';
import { GenericDialog } from '@/components/shared/GenericDialog';
import { CopyTemplateDialog } from './CopyTemplateDialog';
import { abyssColors } from '@/theme/colors';
import { containedButtonStyle } from '@/theme';
import {
  AllLogsTemplatesByTypeDocument,
  useAllStructuresQuery,
  GetVersionsDocument,
  useAddDefaultIndirectPobTemplateMutation,
  useCopyLogsTemplateMutation,
  useSyncLogsTemplatesMutation,
  LogsTemplateDocument,
} from '@/__generated__/graphql';
import { removeTypename, useSnackBarMessage } from '../utils';

type IndirectPobTemplateActionsProps = {
  structureId: string;
  currentStructureName: string;
  hasTemplate: boolean;
  templateForDownload: unknown;
  templateIdForSync: string | undefined;
  linkedStructureIds: string[];
  onAfterAddDefault?: () => void;
};

export const IndirectPobTemplateActions = ({
  structureId,
  currentStructureName,
  hasTemplate,
  templateForDownload,
  templateIdForSync,
  linkedStructureIds,
  onAfterAddDefault,
}: IndirectPobTemplateActionsProps) => {
  const [isAddDefaultOpen, setIsAddDefaultOpen] = useState(false);
  const [isCopyDialogOpen, setIsCopyDialogOpen] = useState(false);
  const [isSyncConfirmOpen, setIsSyncConfirmOpen] = useState(false);

  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });
  const { data: allStructuresData } = useAllStructuresQuery({
    variables: {
      input: {
        isActive: true,
      },
    },
  });

  const linkedStructureNames = useMemo(() => {
    const allStructures = allStructuresData?.allStructures ?? [];
    if (linkedStructureIds.length === 0 || allStructures.length === 0) {
      return [];
    }

    const linkedStructureIdSet = new Set(linkedStructureIds);
    return allStructures
      .filter((structure) => linkedStructureIdSet.has(structure.id))
      .map((structure) => structure.name);
  }, [allStructuresData?.allStructures, linkedStructureIds]);

  const payloadForDownload = useMemo(() => {
    if (templateForDownload === null || templateForDownload === undefined) {
      return undefined;
    }
    return removeTypename(templateForDownload);
  }, [templateForDownload]);

  const downloadJson = useCallback(() => {
    if (!payloadForDownload) {
      showSnackBar('Template data is not loaded yet', 'error');
      return;
    }
    const jsonData = JSON.stringify(payloadForDownload, undefined, 2);
    const jsonBlob = new Blob([jsonData], { type: 'application/json' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(jsonBlob);
    const name =
      typeof payloadForDownload === 'object' &&
      payloadForDownload !== null &&
      'name' in payloadForDownload &&
      typeof (payloadForDownload as { name?: unknown }).name === 'string'
        ? (payloadForDownload as { name: string }).name
        : 'Indirect POB Template';
    const fileName = `${name}-${Date.now().toString()}.json`;
    link.download = fileName;
    document.body.append(link);
    link.click();
    link.remove();
  }, [payloadForDownload, showSnackBar]);

  const [addDefaultTemplate, { loading: isAddingDefault }] =
    useAddDefaultIndirectPobTemplateMutation({
      onCompleted: (response) => {
        if (response?.addDefaultIndirectPobTemplate) {
          showSnackBar('Default Indirect POB template added successfully', 'success');
          setIsAddDefaultOpen(false);
          onAfterAddDefault?.();
        }
      },
      onError: (error: { message: string }) => {
        showSnackBar(error.message, 'error');
        setIsAddDefaultOpen(false);
      },
      refetchQueries: [AllLogsTemplatesByTypeDocument, LogsTemplateDocument],
    });

  const [copyTemplateMutation] = useCopyLogsTemplateMutation({
    refetchQueries: [AllLogsTemplatesByTypeDocument, LogsTemplateDocument, GetVersionsDocument],
  });

  const [syncTemplates, { loading: isSyncing }] = useSyncLogsTemplatesMutation({
    refetchQueries: [AllLogsTemplatesByTypeDocument, LogsTemplateDocument, GetVersionsDocument],
  });

  const handleConfirmAddDefault = useCallback(() => {
    addDefaultTemplate({
      variables: {
        input: {
          structureId,
        },
      },
    });
  }, [addDefaultTemplate, structureId]);

  const handleCopyTemplate = useCallback(
    (sourceStructureId: string, sourceTemplateId: string) => {
      copyTemplateMutation({
        variables: {
          input: {
            sourceStructureId,
            sourceTemplateId,
            targetStructureId: structureId,
          },
        },
        onCompleted: (response) => {
          if (response?.copyLogsTemplate) {
            showSnackBar('Indirect POB template copied successfully', 'success');
            setIsCopyDialogOpen(false);
          }
        },
        onError: (error: { message: string }) => {
          console.error(error);
          showSnackBar(error.message, 'error');
          setIsCopyDialogOpen(false);
        },
      });
    },
    [copyTemplateMutation, structureId, showSnackBar]
  );

  const handleSyncTemplates = useCallback(() => {
    if (!templateIdForSync) {
      showSnackBar('No template selected for sync', 'error');
      return;
    }

    setIsSyncConfirmOpen(false);
    syncTemplates({
      variables: {
        input: {
          structureId,
          templateId: templateIdForSync,
        },
      },
      onCompleted: (response) => {
        if (response?.syncLogsTemplates) {
          showSnackBar('Indirect POB templates synced successfully', 'success');
        }
      },
      onError: (error: { message: string }) => {
        console.error(error);
        showSnackBar(error.message, 'error');
      },
    });
  }, [syncTemplates, structureId, templateIdForSync, showSnackBar]);

  return (
    <>
      <Stack
        direction="row"
        gap={2}
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'flex-end',
          width: '100%',
        }}
      >
        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ justifyContent: 'flex-end' }}>
          {hasTemplate ? (
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Tooltip title="This will sync linked structures" arrow placement="top">
                <Button
                  startIcon={
                    isSyncing ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />
                  }
                  variant="contained"
                  onClick={() => setIsSyncConfirmOpen(true)}
                  sx={containedButtonStyle}
                  disabled={isSyncing || linkedStructureIds.length === 0}
                >
                  Sync Templates
                </Button>
              </Tooltip>
              <Button
                startIcon={<DownloadIcon />}
                variant="contained"
                onClick={() => downloadJson()}
                sx={containedButtonStyle}
              >
                Download JSON
              </Button>
            </Stack>
          ) : (
            <>
              <Button
                startIcon={<AddCircleIcon />}
                variant="contained"
                sx={containedButtonStyle}
                onClick={() => setIsAddDefaultOpen(true)}
              >
                Add Default Indirect POB Template
              </Button>
              <Button
                startIcon={<ContentCopyIcon />}
                variant="contained"
                sx={containedButtonStyle}
                onClick={() => setIsCopyDialogOpen(true)}
              >
                Copy Indirect POB Template
              </Button>
            </>
          )}
        </Stack>
      </Stack>

      <GenericDialog
        open={isSyncConfirmOpen}
        onClose={() => setIsSyncConfirmOpen(false)}
        title="Sync Templates"
        content={
          <Box>
            <Typography fontWeight={400} fontSize="1.4rem" color={abyssColors.primary[500]}>
              The following structures will be synced with {currentStructureName}.
            </Typography>
            <Box component="ul" sx={{ my: 2, pl: 3 }}>
              {linkedStructureNames.map((structureName) => (
                <Typography
                  key={structureName}
                  component="li"
                  fontWeight={400}
                  fontSize="1.4rem"
                  color={abyssColors.primary[500]}
                >
                  {structureName}
                </Typography>
              ))}
            </Box>
            <Typography fontWeight={400} fontSize="1.4rem" color={abyssColors.primary[500]}>
              Are you sure you want to continue?
            </Typography>
          </Box>
        }
        actions={
          <>
            <Button variant="text" onClick={() => setIsSyncConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSyncTemplates}
              disabled={isSyncing}
              sx={containedButtonStyle}
            >
              Confirm
            </Button>
          </>
        }
        actionsStyles={{ mr: 3, pb: 3 }}
      />

      <GenericDialog
        open={isAddDefaultOpen}
        onClose={() => setIsAddDefaultOpen(false)}
        title="Add Default Indirect POB Template"
        content={
          <Typography fontWeight={400} fontSize="1.4rem" color={abyssColors.primary[500]}>
            This will create the default Indirect POB template fields for this structure. Continue?
          </Typography>
        }
        actions={
          <>
            <Button variant="text" onClick={() => setIsAddDefaultOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirmAddDefault}
              disabled={isAddingDefault}
              sx={containedButtonStyle}
            >
              Confirm
            </Button>
          </>
        }
        actionsStyles={{ mr: 3, pb: 3 }}
      />

      <CopyTemplateDialog
        open={isCopyDialogOpen}
        onClose={() => setIsCopyDialogOpen(false)}
        onCopy={handleCopyTemplate}
        currentStructureId={structureId}
      />
    </>
  );
};
